<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  Check,
  CircleAlert,
  Download,
  LoaderCircle,
  LogOut,
  Mic2,
  Music2,
  Share2,
  Sparkles,
  WandSparkles,
} from "@lucide/vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { MobileUser, Track } from "@/types";

const title = ref("");
const prompt = ref("");
const lyrics = ref("");
const instrumental = ref(false);
const generatingLyrics = ref(false);
const generatingTitle = ref(false);
const generatingDescription = ref(false);
const generatingMusic = ref(false);
const error = ref("");
const result = ref<Track | null>(null);
const style = ref("城市流行");
const generatedTitleHistory = ref<string[]>([]);
const titleGenerationCount = ref(0);

const mobileUser = ref<MobileUser | null>(null);
const authReady = ref(false);
const authTab = ref<"login" | "register">("login");
const authUsername = ref("");
const authPassword = ref("");
const authBusy = ref(false);
const authError = ref("");
const authMessage = ref("");
const registered = ref(false);

const remaining = computed(() =>
  mobileUser.value
    ? Math.max(
        0,
        mobileUser.value.dailyRemaining
          ?? mobileUser.value.quota - (mobileUser.value.dailyUsed ?? 0),
      )
    : 0,
);
const quotaExhausted = computed(
  () => Boolean(mobileUser.value) && remaining.value <= 0,
);

type VocalStyle = "female-clear" | "female-powerful" | "male-warm" | "male-deep";

const vocalStyle = ref<VocalStyle>("female-clear");
const vocalStyles: Array<{
  id: VocalStyle;
  label: string;
  detail: string;
  prompt: string;
}> = [
  { id: "female-clear", label: "清透女声", detail: "轻盈 · 亲密", prompt: "清透温暖女声，轻盈且有亲密感" },
  { id: "female-powerful", label: "力量女声", detail: "宽广 · 爆发", prompt: "力量感女声，音域宽广，副歌有爆发力" },
  { id: "male-warm", label: "温暖男声", detail: "真诚 · 中低音", prompt: "温暖真诚男声，中低音饱满，演唱自然" },
  { id: "male-deep", label: "低沉男声", detail: "磁性 · 克制", prompt: "低沉磁性男声，声线有颗粒感，演唱克制" },
];

const presets = [
  { label: "城市流行", value: "华语独立流行，夜晚城市，克制而有力量，温暖合成器，副歌有记忆点" },
  { label: "松弛爵士", value: "Neo Soul，爵士和弦，慵懒鼓组，深夜酒吧，温柔女声" },
  { label: "热血摇滚", value: "华语摇滚，现场感鼓组，失真吉他，充满希望，万人合唱副歌" },
  { label: "氛围电子", value: "Ambient Electronic，未来感，颗粒合成器，宽广空间，纯音乐" },
];

const canGenerate = computed(() =>
  Boolean(title.value && prompt.value && !quotaExhausted.value),
);

function withSelectedVocal(value: string) {
  if (instrumental.value) return value;
  const selected = vocalStyles.find((item) => item.id === vocalStyle.value) || vocalStyles[0];
  const cleaned = value
    .replace(/\b(?:male|female)\s+vocals?\b/gi, "")
    .replace(/(?:清透|清亮|温柔|力量|高亢|温暖|低沉|磁性)?[男女]声(?:主唱|演唱|音色)?/g, "")
    .replace(/[，,]{2,}/g, "，")
    .replace(/^[，,\s]+|[，,\s]+$/g, "");
  return `${cleaned}，${selected.prompt}`;
}

function choosePreset(item: (typeof presets)[number]) {
  style.value = item.label;
  instrumental.value = item.label === "氛围电子";
  prompt.value = withSelectedVocal(item.value);
}

function chooseVocal(id: VocalStyle) {
  vocalStyle.value = id;
  instrumental.value = false;
  if (prompt.value) prompt.value = withSelectedVocal(prompt.value);
}

async function refreshSession() {
  try {
    const session = await api.mobile.session();
    mobileUser.value = session.authenticated ? session.user || null : null;
  } catch {
    mobileUser.value = null;
  } finally {
    authReady.value = true;
  }
}

async function login() {
  authError.value = "";
  authMessage.value = "";
  authBusy.value = true;
  try {
    const data = await api.mobile.login(authUsername.value.trim(), authPassword.value);
    mobileUser.value = data.user;
  } catch (cause) {
    authError.value = cause instanceof Error ? cause.message : "登录失败";
  } finally {
    authBusy.value = false;
  }
}

async function register() {
  authError.value = "";
  authMessage.value = "";
  authBusy.value = true;
  try {
    const data = await api.mobile.register(authUsername.value.trim(), authPassword.value);
    authMessage.value = data.message || "申请已提交，等待管理员审核。";
    registered.value = true;
  } catch (cause) {
    authError.value = cause instanceof Error ? cause.message : "注册失败";
  } finally {
    authBusy.value = false;
  }
}

async function logoutMobile() {
  try {
    await api.mobile.logout();
  } catch {
    // 退出失败也按未登录处理。
  }
  mobileUser.value = null;
  result.value = null;
  authTab.value = "login";
  authUsername.value = "";
  authPassword.value = "";
  authError.value = "";
  authMessage.value = "";
  registered.value = false;
}

async function generateTitle() {
  error.value = "";
  generatingTitle.value = true;
  try {
    const selected = presets.find((item) => item.label === style.value) || presets[0];
    style.value = selected.label;
    const data = await api.mobile.songTitle({
      prompt: withSelectedVocal(`${selected.label}。${prompt.value || selected.value}`),
      lyrics: lyrics.value || undefined,
      instrumental: instrumental.value,
      excludedTitles: [title.value, ...generatedTitleHistory.value].filter(Boolean),
      variation: titleGenerationCount.value,
    });
    title.value = data.title;
    prompt.value = withSelectedVocal(data.description);
    titleGenerationCount.value += 1;
    generatedTitleHistory.value = [
      data.title,
      ...generatedTitleHistory.value.filter((item) => item !== data.title),
    ].slice(0, 12);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "AI 取名失败";
  } finally {
    generatingTitle.value = false;
  }
}

async function generateDescription() {
  error.value = "";
  generatingDescription.value = true;
  try {
    const selected = presets.find((item) => item.label === style.value) || presets[0];
    style.value = selected.label;
    const data = await api.mobile.songTitle({
      title: title.value || undefined,
      prompt: withSelectedVocal(`${selected.label}。${prompt.value || selected.value}`),
      lyrics: lyrics.value || undefined,
      instrumental: instrumental.value,
    });
    prompt.value = withSelectedVocal(data.description);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "AI 生成描述失败";
  } finally {
    generatingDescription.value = false;
  }
}

async function createLyrics() {
  error.value = "";
  generatingLyrics.value = true;
  try {
    const data = await api.mobile.lyrics({
      prompt: `为歌曲《${title.value || "未命名"}》创作完整歌词。${prompt.value}`,
      title: title.value || undefined,
    });
    if (data.songTitle && !title.value) title.value = data.songTitle;
    lyrics.value = data.lyrics;
    if (data.styleTags) prompt.value = `${data.styleTags}。${prompt.value}`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "歌词生成失败";
  } finally {
    generatingLyrics.value = false;
  }
}

async function createMusic() {
  error.value = "";
  result.value = null;
  generatingMusic.value = true;
  try {
    const data = await api.mobile.music({
      title: title.value,
      prompt: prompt.value,
      lyrics: lyrics.value,
      instrumental: instrumental.value,
      vocalStyle: vocalStyle.value,
    });
    result.value = data.track;
    mobileUser.value = data.user;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "音乐生成失败";
  } finally {
    generatingMusic.value = false;
  }
}

async function share() {
  if (!result.value) return;
  const url = `${location.origin}/m/listen/${result.value.id}`;
  const data = { title: result.value.title, url };
  if (navigator.share) await navigator.share(data);
  else {
    await navigator.clipboard.writeText(url);
    alert("链接已复制");
  }
}

onMounted(refreshSession);
</script>

<template>
  <main class="m-page">
    <header class="m-header">
      <span class="brand-mark"><Music2 :size="19" /></span>
      <div>
        <strong>声浪工坊</strong>
        <small>AI 音乐创作</small>
      </div>
      <template v-if="mobileUser">
        <span class="m-quota" :class="{ exhausted: quotaExhausted }">
          今日剩余 {{ remaining }} / {{ mobileUser.quota }} 次
        </span>
        <button class="m-logout" type="button" aria-label="退出登录" @click="logoutMobile">
          <LogOut :size="16" />
        </button>
      </template>
      <span v-else class="m-engine">MUSIC 3.0</span>
    </header>

    <section v-if="!authReady" class="m-form">
      <p class="m-footnote">正在检查登录状态…</p>
    </section>

    <section v-else-if="!mobileUser" class="m-auth">
      <template v-if="registered">
        <div class="m-auth-done">
          <span><Check :size="26" /></span>
          <h1>申请已提交</h1>
          <p>{{ authMessage || "管理员审核通过后即可登录创作。" }}</p>
          <button class="m-button m-button-dark" type="button" @click="registered = false; authTab = 'login'">
            返回登录
          </button>
        </div>
      </template>
      <template v-else>
        <div class="m-tabs">
          <button type="button" :class="{ active: authTab === 'login' }" @click="authTab = 'login'">登录</button>
          <button type="button" :class="{ active: authTab === 'register' }" @click="authTab = 'register'">注册申请</button>
        </div>

        <form @submit.prevent="authTab === 'login' ? login() : register()">
          <div class="m-field">
            <label for="m-auth-username">用户名</label>
            <input
              id="m-auth-username"
              v-model="authUsername"
              autocomplete="username"
              placeholder="2–30 个字符，字母或中文开头"
            />
          </div>
          <div class="m-field">
            <label for="m-auth-password">密码</label>
            <input
              id="m-auth-password"
              v-model="authPassword"
              type="password"
              :autocomplete="authTab === 'login' ? 'current-password' : 'new-password'"
              placeholder="至少 6 位"
            />
          </div>

          <div v-if="authError" class="m-error">
            <CircleAlert :size="18" />
            <span>{{ authError }}</span>
          </div>

          <button class="m-generate" type="submit" :disabled="authBusy || !authUsername || !authPassword">
            <LoaderCircle v-if="authBusy" class="spin" :size="19" />
            <Sparkles v-else :size="19" />
            {{ authTab === "login" ? "登录并开始创作" : "提交注册申请" }}
          </button>
          <p class="m-footnote">
            {{ authTab === "login" ? "新用户请先注册，审核通过后才能登录。" : "提交后等待管理员审核，默认每天可生成 2 次。" }}
          </p>
        </form>
      </template>
    </section>

    <section v-else-if="result" class="m-result">
      <TrackArtwork :title="result.title" />
      <p class="eyebrow">生成成功</p>
      <h1>{{ result.title }}</h1>
      <p class="m-result-meta">
        {{ result.artist }} · {{ result.genre }} · {{ Math.ceil(result.durationMs / 1000) }} 秒 ·
        {{ result.audioFormat.toUpperCase() }}
      </p>
      <audio :src="result.audioUrl" controls autoplay />
      <div class="m-result-actions">
        <a class="m-button m-button-lime" :href="result.audioUrl" download>
          <Download :size="17" />下载母带
        </a>
        <button class="m-button m-button-ghost" type="button" @click="share">
          <Share2 :size="17" />分享
        </button>
      </div>
      <button class="m-button m-button-dark" type="button" :disabled="quotaExhausted" @click="result = null">
        <Sparkles :size="17" />{{ quotaExhausted ? "今日次数已用完" : "再创作一首" }}
      </button>
    </section>

    <section v-else class="m-form">
      <div class="m-field">
        <div class="m-field-head">
          <label for="m-title">作品名 <small>{{ title.length }}/80</small></label>
          <button class="m-magic" type="button" :disabled="generatingTitle" @click="generateTitle">
            <LoaderCircle v-if="generatingTitle" class="spin" :size="15" />
            <WandSparkles v-else :size="15" />
            {{ generatingTitle ? "取名中…" : "AI 取名" }}
          </button>
        </div>
        <input id="m-title" v-model="title" maxlength="80" placeholder="给这首歌一个名字" />
      </div>

      <div class="m-field">
        <span>快速风格</span>
        <div class="m-chips">
          <button
            v-for="item in presets"
            :key="item.label"
            type="button"
            class="m-chip"
            :class="{ active: style === item.label }"
            @click="choosePreset(item)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <div class="m-field" :class="{ muted: instrumental }">
        <span>人声音色 <small v-if="instrumental">纯音乐模式已停用</small></span>
        <div class="m-vocals">
          <button
            v-for="voice in vocalStyles"
            :key="voice.id"
            type="button"
            class="m-vocal"
            :class="{ active: vocalStyle === voice.id }"
            :disabled="instrumental"
            @click="chooseVocal(voice.id)"
          >
            <span><Mic2 :size="16" /><strong>{{ voice.label }}</strong><small>{{ voice.detail }}</small></span>
            <Check v-if="vocalStyle === voice.id" :size="15" />
          </button>
        </div>
      </div>

      <div class="m-field">
        <div class="m-field-head">
          <label for="m-prompt">音乐描述 <small>{{ prompt.length }}/2000</small></label>
          <button class="m-magic" type="button" :disabled="generatingDescription || generatingTitle" @click="generateDescription">
            <LoaderCircle v-if="generatingDescription" class="spin" :size="15" />
            <WandSparkles v-else :size="15" />
            {{ generatingDescription ? "构思中…" : "AI 生成描述" }}
          </button>
        </div>
        <textarea id="m-prompt" v-model="prompt" maxlength="2000" rows="4" placeholder="曲风、情绪、乐器、速度、场景……" />
        <em>写得具体更好：曲风 + 情绪 + 乐器 + 人声 + 场景</em>
      </div>

      <div class="m-toggle">
        <span>
          <Music2 :size="18" />
          <span><strong>纯音乐模式</strong><small>关闭人声与歌词</small></span>
        </span>
        <button class="m-switch" :class="{ active: instrumental }" role="switch" :aria-checked="instrumental" @click="instrumental = !instrumental"><i /></button>
      </div>

      <div class="m-field" :class="{ muted: instrumental }">
        <div class="m-field-head">
          <span>歌词</span>
          <button class="m-magic" type="button" :disabled="instrumental || generatingLyrics" @click="createLyrics">
            <LoaderCircle v-if="generatingLyrics" class="spin" :size="15" />
            <WandSparkles v-else :size="15" />
            {{ generatingLyrics ? "写词中…" : "AI 帮我写词" }}
          </button>
        </div>
        <textarea v-model="lyrics" :disabled="instrumental" maxlength="3500" rows="8" placeholder="从第一句开始写下故事……" />
        <div class="m-char-line">
          <span>AI 会自动整理自然段</span>
          <span>{{ lyrics.length }}/3500</span>
        </div>
      </div>

      <div v-if="error" class="m-error">
        <CircleAlert :size="18" />
        <span>{{ error }}</span>
      </div>

      <div class="m-generate-dock">
        <button
          class="m-generate"
          type="button"
          :disabled="generatingMusic || !canGenerate"
          @click="createMusic"
        >
          <LoaderCircle v-if="generatingMusic" class="spin" :size="19" />
          <Sparkles v-else :size="19" />
          {{ generatingMusic ? "正在编曲与演唱（通常需要数分钟）" : quotaExhausted ? "今日次数已用完，明天可继续" : canGenerate ? "生成完整音乐" : "先写作品名和音乐描述" }}
        </button>
        <p class="m-footnote">每次生成占用今日 1 次额度；生成失败会自动退回，仅成功作品计入累计次数。</p>
      </div>
    </section>

    <footer class="m-footer">由声浪工坊 × MiniMax Music 3.0 创作 · AI 辅助音乐作品</footer>
  </main>
</template>
